'use client';
import Link from 'next/link';
import { useTranslation } from '@/hooks/useTranslation';
import { useCallback, useEffect, useState } from 'react';
import { HiOutlineArrowRight, HiOutlineSparkles, HiOutlineTrophy, HiXMark } from 'react-icons/hi2';
import { MdOutlineCreditCardOff } from 'react-icons/md';
import { AiOutlineThunderbolt } from 'react-icons/ai';
import { ILinks } from '@/hooks/useLinks';
import axios from 'axios';

type OfferBannerProps = {
  setShow: any;
  links: ILinks;
  locale: string;
};

export default function OfferBanner({ setShow, links, locale }: OfferBannerProps) {
  const { t } = useTranslation(locale);
  const [isVisible, setIsVisible] = useState(false); // Changed to false initially
  const [currentOffer, setCurrentOffer] = useState(0);
  const [liveUsers, setLiveUsers] = useState('🌐');

  // Rotating offers for better engagement
  const offers = [
    {
      icon: AiOutlineThunderbolt,
      text: t('Layout/Banner/key1'),
      highlight: t('Layout/Banner/badge-key1'),
      gradient: 'tw-from-blue-600 tw-to-purple-600',
    },
    {
      icon: HiOutlineSparkles,
      text: t('Layout/Banner/key2'),
      highlight: t('Layout/Banner/badge-key2'),
      gradient: 'tw-from-green-600 tw-to-emerald-600',
    },
    {
      icon: HiOutlineTrophy,
      text: t('Layout/Banner/key3'),
      highlight: t('Layout/Banner/badge-key3'),
      gradient: 'tw-from-purple-600 tw-to-pink-600',
    },
  ];

  // Show banner after 5 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 5000); // 5 seconds delay

    return () => clearTimeout(timer);
  }, []);

  const fetchLiveUsers = useCallback(async (signal) => {
    try {
      const response = await axios.get(`/service/api/stats`, {
        signal,
        timeout: 5000,
      });

      setLiveUsers(response.data?.live || '🌐');
      return true;
    } catch (error: any) {
      if (error.name === 'AbortError') {
        return false;
      }
      console.error('Failed to fetch live users', error);
      return false;
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let intervalId;

    fetchLiveUsers(controller.signal);

    intervalId = setInterval(() => {
      if (!controller.signal.aborted) {
        fetchLiveUsers(controller.signal);
      }
    }, 20000);

    return () => {
      controller.abort();
      clearInterval(intervalId);
    };
  }, [fetchLiveUsers]);

  // Auto-rotate offers every 8 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentOffer((prev) => (prev + 1) % offers.length);
    }, 8000);

    return () => clearInterval(interval);
  }, [offers.length]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => setShow(false), 300); // Allow fade out animation
  };

  const currentOfferData = offers[currentOffer];

  return (
    <div
      className={`tw-relative tw-w-full tw-transition-all tw-duration-300 tw-ease-in-out ${
        isVisible ? 'tw-opacity-100 tw-max-h-16' : 'tw-opacity-0 tw-max-h-0'
      } tw-overflow-hidden`}
    >
      {/* Main Banner */}
      <div
        className={`tw-relative tw-bg-gradient-to-r ${currentOfferData.gradient} tw-text-white tw-py-3 tw-px-4 tw-shadow-lg`}
      >
        {/* Animated background pattern */}
        <div className="tw-absolute tw-inset-0 tw-opacity-10">
          <div
            className="tw-absolute tw-inset-0"
            style={{
              backgroundImage: `radial-gradient(circle at 20px 20px, rgba(255,255,255,0.3) 1px, transparent 0)`,
              backgroundSize: '40px 40px',
              animation: 'slide 20s linear infinite',
            }}
          ></div>
        </div>

        {/* Content */}
        <div className="tw-relative tw-max-w-7xl tw-mx-auto tw-flex tw-items-center tw-justify-center tw-gap-4">
          {/* Icon with pulse animation */}
          <div className="tw-hidden sm:tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-bg-white/20 tw-rounded-full tw-backdrop-blur-sm">
            <currentOfferData.icon className="tw-w-5 tw-h-5 tw-text-white tw-animate-pulse" />
          </div>

          {/* Main content */}
          <Link
            href={links.register.href}
            className="tw-group tw-flex tw-items-center tw-gap-3 tw-text-center tw-transition-all tw-duration-300 hover:tw-scale-105"
          >
            <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap tw-justify-center">
              {/* Highlight badge for mobile */}
              <span className="tw-inline-flex tw-items-center tw-gap-1 tw-bg-white/20 tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-font-bold tw-backdrop-blur-sm sm:tw-hidden">
                <currentOfferData.icon className="tw-w-3 tw-h-3" />
                {currentOfferData.highlight}
              </span>

              {/* Main text */}
              <span className="tw-text-sm sm:tw-text-base tw-font-semibold tw-hidden sm:tw-inline">
                {currentOfferData.text}
              </span>

              {/* Mobile text */}
              <span className="tw-text-sm tw-font-medium tw-block sm:tw-hidden">{t('Layout/Banner/mobile-text')}</span>

              {/* Highlight for desktop */}
              <span className="tw-hidden sm:tw-inline-flex tw-items-center tw-gap-1 tw-bg-white/20 tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-font-bold tw-backdrop-blur-sm">
                {currentOfferData.highlight}
              </span>
            </div>

            {/* Arrow with animation */}
            <HiOutlineArrowRight className="tw-w-4 tw-h-4 tw-transition-transform tw-duration-300 group-hover:tw-translate-x-1" />
          </Link>

          {/* Progress indicators */}
          <div className="tw-hidden md:tw-flex tw-items-center tw-gap-2 tw-ml-4">
            {offers.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentOffer(index)}
                className={`tw-w-2 tw-h-2 tw-rounded-full tw-transition-all tw-duration-300 ${
                  index === currentOffer ? 'tw-bg-white tw-scale-125' : 'tw-bg-white/50 hover:tw-bg-white/70'
                }`}
                aria-label={`Switch to offer ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Close button */}
        <button
          onClick={handleClose}
          className="tw-absolute tw-right-3 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-w-8 tw-h-8 tw-rounded-full tw-bg-white/10 tw-backdrop-blur-sm tw-flex tw-items-center tw-justify-center tw-transition-all tw-duration-300 hover:tw-bg-white/20 hover:tw-scale-110 tw-group"
          aria-label="Close banner"
        >
          <HiXMark className="tw-w-4 tw-h-4 tw-text-white/70 group-hover:tw-text-white" />
        </button>

        {/* Additional trust elements */}
        <div className="tw-absolute tw-left-4 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-hidden lg:tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-white/80">
          <MdOutlineCreditCardOff className="tw-w-4 tw-h-4" />
          <span>{t('Layout/Banner/no-credit-card')}</span>
        </div>

        {/* Urgency indicator */}
        <div className="tw-absolute tw-right-16 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-hidden xl:tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-white/80">
          <div className="tw-w-2 tw-h-2 tw-bg-green-400 tw-rounded-full tw-animate-pulse"></div>
          <span>
            {liveUsers} {t('Layout/Banner/live-users')}
          </span>
        </div>

        {/* CSS for animations */}
        <style jsx>{`
          @keyframes slide {
            0% {
              transform: translateX(0);
            }
            100% {
              transform: translateX(-40px);
            }
          }

          @keyframes fadeInSlide {
            from {
              opacity: 0;
              transform: translateY(-20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}</style>
      </div>
    </div>
  );
}
